import { useContext, useEffect, useMemo, useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Table } from "react-bootstrap";
import toast from "react-hot-toast";

import UserSearchForm from "../../../../components/Users/UserSearchForm";
import request from "../../../../utils/request";
import { statusCheckHandler } from "./pathologyStatusCheckHandler";
import { UserContext } from "../../../../Context/UserContextAPI";
import Loading from "../../../../utils/Loader/Loading";

const CombineMedicalPathology = () => {
  const { currentUser } = useContext(UserContext);

  /* ------------------------- FILTER STATE ------------------------- */
  const [filterData, setFilterData] = useState({
    fromDate: new Date().toISOString().split("T")[0],
    toDate: new Date().toISOString().split("T")[0],
  });

  /* --------------------- EDITABLE TABLE STATE ---------------------- */
  const [pathologyReportData, setPathologyReportData] = useState([]);

  const [editedUsers, setEditedUsers] = useState(new Set());

  /* --------------------------- QUERIES ----------------------------- */
  const { data, isPending: isLoadingUsers } = useQuery({
    queryKey: ["users", "pathology", filterData],
    queryFn: async () => {
      const query = new URLSearchParams(filterData).toString();
      const response = await request.getAll(
        `v2/reports/results/users/pathology?${query}`,
      );

      if (response.status !== "OK") {
        toast.error("Server error occurred.");
      }

      return response.data || [];
    },
  });
  console.log(data);
  const userData = data?.users || [];
  const pathologyInvestigation = data?.investigation;

  const getInvestigationIds = (user) => {
    const detail = user.userDetail;

    if (user.reInvstIds) {
      return user.reInvstIds.split(",").map(Number);
    }

    if (user.role_id === 5) {
      return (user.tests || []).map((t) => t.investigation_id);
    }

    return detail?.package?.investigation_id
      ? detail.package.investigation_id.split(",").map(Number)
      : [];
  };

  /* ------------------- INITIALIZE EDIT STATE ---------------------- */

  useEffect(() => {
    if (!userData.length || !pathologyInvestigation.length) return;

    const initialState = userData.map((user) => {
      const pathologyTests = user.tests || [];

      // 👉 decide which investigations belong to this user
      const invIds = getInvestigationIds(user);

      // 👉 filter master investigation list
      const filteredInvestigations = pathologyInvestigation.filter((inv) =>
        invIds.includes(inv.id),
      );

      const data = filteredInvestigations.map((invTest) => {
        const test = pathologyTests.find(
          (t) => t.investigation_id === invTest.id,
        );

        return {
          user_id: user.id,
          investigation_id: invTest.id,
          test_id:
            test?.test_id || invTest?.investigation_tests?.[0]?.id || null,
          result: test?.result || "",
          user_file: test?.user_file || "",
          created_by: currentUser?.f_name,
        };
      });

      return {
        userId: user.id,
        data,
      };
    });

    setPathologyReportData(initialState);
  }, [userData, pathologyInvestigation, currentUser?.f_name]);

  /* ------------------ FAST LOOKUP MAP (IMPORTANT) ------------------ */
  const pathologyMap = useMemo(() => {
    const map = {};
    pathologyReportData.forEach((user) => {
      map[user.userId] = {};
      user.data.forEach((item) => {
        map[user.userId][item.investigation_id] = item;
      });
    });
    return map;
  }, [pathologyReportData]);

  /* --------------------- CHANGE HANDLER ---------------------------- */
  const handleChange = useCallback((payload) => {
    setPathologyReportData((prev) =>
      prev.map((user) =>
        user.userId !== payload.user_id
          ? user
          : {
              ...user,
              data: user.data.map((item) =>
                item.investigation_id === payload.investigation_id
                  ? { ...item, result: payload.result }
                  : item,
              ),
            },
      ),
    );
    setEditedUsers((prev) => {
      const next = new Set(prev);
      next.add(payload.user_id);
      return next;
    });
  }, []);

  const handleSaveData = async ({ isDraftSave = false }) => {
    try {
      const filtered = pathologyReportData.filter((u) =>
        editedUsers.has(u.userId),
      );

      if (!filtered.length) {
        toast.error("No changes to save.");
        return;
      }

      const res = await request.create(
        "v2/reports/results/users/pathology/combine",
        {
          userPathologyData: filtered,
          isDraftSave: isDraftSave,
        },
      );
      // if (res.status === "OK") {
      //   toast.success("Changes saved successfully.");
      // } else {
      //   toast.error("Failed to save changes.");
      // }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="card medical-report-container">
      <div className="card-body">
        <div className="border p-3 rounded mb-3">
          <h6 className="btn btn-danger text-white fw-bold">
            Combine Medical Pathology
          </h6>

          <UserSearchForm userSearchHandler={(val) => setFilterData(val)} />
        </div>

        {isLoadingUsers ? (
          <Loading />
        ) : (
          <div>
            <Table
              striped
              bordered
              hover
              responsive
              className="combine-pathology-table"
            >
              <thead style={{ backgroundColor: "teal", color: "white" }}>
                <tr>
                  <th>Name</th>
                  <th>REG No</th>
                  {pathologyInvestigation.map((inv) => (
                    <th key={inv.id}>{inv.name}</th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {userData.map((user) => (
                  <tr key={user.id}>
                    <td>{user.f_name}</td>
                    <td>{user.reg_prefix + user.registration_no}</td>

                    {pathologyInvestigation.map((investigation) => {
                      const isMaleAndPregnancyTest =
                        (user.userDetail?.gender?.name === "Male" ||
                          user.userDetail?.gender?.name === "male" ||
                          user.userDetail?.gender_id === 1) &&
                        investigation?.name === "Pregnancy Test";

                      const report = pathologyMap[user.id]?.[investigation.id];

                      const { reports } = statusCheckHandler([
                        {
                          ...investigation,
                          investigation_id: investigation.id,
                          result: report?.result,
                        },
                      ]);

                      const status = reports?.[0]?.status;
                      const textColor =
                        status === 2
                          ? "text-success"
                          : status === 3
                            ? "text-danger"
                            : status === 4
                              ? "text-primary"
                              : "";

                      const isDropdown =
                        investigation.investigation_tests[0]?.value_type?.toLowerCase() ===
                        "dropdown";

                      const hasTest = pathologyReportData.find(
                        (u) =>
                          u.userId === user.id &&
                          u.data.some(
                            (item) =>
                              item.investigation_id === investigation.id,
                          ),
                      );

                      if (!hasTest || isMaleAndPregnancyTest) {
                        return <td key={investigation.id}></td>;
                      }

                      return (
                        <td key={investigation.id}>
                          {isDropdown ? (
                            <select
                              className={`form-select w-75 ${textColor}`}
                              value={report?.result || ""}
                              onChange={(e) =>
                                handleChange({
                                  user_id: user.id,
                                  investigation_id: investigation.id,
                                  result: e.target.value,
                                })
                              }
                            >
                              {/* <option value="">Select</option>*/}
                              {investigation.investigation_tests[0]?.dropdown_value
                                ?.split(",")
                                .map((v, i) => (
                                  <option key={i} value={v}>
                                    {v}
                                  </option>
                                ))}
                            </select>
                          ) : (
                            <input
                              type="text"
                              className={`form-control ${textColor}`}
                              value={report?.result || ""}
                              onChange={(e) =>
                                handleChange({
                                  user_id: user.id,
                                  investigation_id: investigation.id,
                                  result: e.target.value,
                                })
                              }
                            />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        <div className="d-flex gap-3 justify-content-end mt-3">
          <Button
            variant="danger"
            onClick={() => {
              window.close();
            }}
          >
            Close
          </Button>
          <Button
            variant="success"
            onClick={() => {
              handleSaveData({ isDraftSave: true });
            }}
          >
            Save Draft
          </Button>
          <Button
            onClick={() => {
              handleSaveData({ isDraftSave: false });
            }}
          >
            Save Data
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CombineMedicalPathology;
